import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, StyleSheet, View } from 'react-native';
import { OutPortal } from 'react-reverse-portal';
import { useIsFocused } from '@react-navigation/native';
import { useRecoilState } from 'recoil';
import { PortalDestination } from 'rn-native-portals';

import { livePageTestIDs, myWeatherPageTestIDs, Theme } from '@constants';
import { useIsVideoFocused, useVideoNodePortal } from '@hooks';
import { navigationService } from '@services';
import { liveVideoPlayerState } from '@store';

import sadCloud from '../../../assets/sad_cloud.png';
import { getTizenVersion, isAndroid, isTizen, isVizio, isWeb, isX1 } from '../../../helpers/Platform';
import { VideoContext } from '../../../hooks/useIsVideoFocused';
import { TWCTile } from '../../focusable/TWCTile/TWCTile';
import { RadialGradientBackground } from '../../loading/RadialGradientBackground';
import { TWCText } from '../../primitives/TWCText/TWCText';

const errorTPath = 'errors.';

const LiveVideoTileAPI = properties => {
  const [{ data, error, isLoading, adTrackingData }, setLiveVideoState] = useRecoilState(liveVideoPlayerState);
  const videoIsFocused = useIsVideoFocused(VideoContext.myWeather);
  const isPageFocused = useIsFocused();

  useEffect(() => {
    if (isPageFocused) {
      const { width, height } = properties;

      setLiveVideoState(currentState => ({
        ...currentState,
        width,
        height,
        stopped: false,
        ...(isAndroid && {
          isFullScreen: false,
          isScreenFocused: true,
          nextFocusUp: null,
        }),
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPageFocused]);

  return (
    <LiveVideoTileUI
      adData={adTrackingData}
      data={data}
      error={error}
      isLoading={isLoading}
      videoIsFocused={videoIsFocused}
      {...properties}
    />
  );
};

const tPath = 'liveVideoTile.';

const LiveVideoTileUI = ({
  adData,
  data,
  error,
  nextFocusDown,
  nextFocusLeft,
  nextFocusRight,
  nextFocusUp,
  isLoading,
  videoIsFocused,
  height,
  width,
  translation,
  showBackground,
}) => {
  const { t } = useTranslation();
  const liveVideoPlayerNode = useVideoNodePortal();

  return (
    <TWCTile
      accessibilityHint={t(`${tPath}tileAccessibilityHint`)}
      accessibilityLabel={t(`${tPath}tileAccessibilityLabel`)}
      height={height}
      margin={0}
      nextFocusDown={nextFocusDown}
      nextFocusLeft={nextFocusLeft}
      nextFocusRight={nextFocusRight}
      nextFocusUp={nextFocusUp}
      padding={0}
      testID={myWeatherPageTestIDs.liveVideoPlayerTile}
      useLinearGradient={false}
      width={width}
      onPress={() => {
        navigationService.navigate(livePageTestIDs.livePage, {
          backButtonDestination: myWeatherPageTestIDs.myWeatherPage,
        });
      }}
    >
      <RadialGradientBackground style={{ borderRadius: Theme.tileBorderRadius }}>
        <View
          collapsable={false}
          importantForAccessibility={'no-hide-descendants'}
          style={styles.radialGradientBackgroundContainer}
        >
          {error ? (
            <View style={styles.videoErrorWrapper}>
              <Image source={sadCloud} style={styles.videoErrorImage} />
              <TWCText noTestID style={styles.videoErrorText} variant={'mediumText24'}>
                {t(`${errorTPath}noDataText`)}
              </TWCText>
            </View>
          ) : (
            <View collapsable={false} style={styles.videoTileWrapper}>
              {videoIsFocused ? (
                <>
                  {isWeb ? (
                    <OutPortal
                      adData={adData}
                      data={data}
                      error={error}
                      height={Theme.px(height)}
                      isFullScreen={false}
                      isLoading={isLoading}
                      isScreenFocused
                      node={liveVideoPlayerNode}
                      showBackground={showBackground}
                      translation={translation}
                      width={Theme.px(width)}
                    />
                  ) : (
                    <PortalDestination
                      collapsable={false}
                      height={height}
                      name={'liveVideoPlayer'}
                      style={{
                        width: Theme.px(width),
                        height: Theme.px(height),
                      }}
                      width={width}
                    />
                  )}
                </>
              ) : null}
            </View>
          )}
        </View>
      </RadialGradientBackground>
    </TWCTile>
  );
};

export const LiveVideoTile = {
  API: LiveVideoTileAPI,
  UI: LiveVideoTileUI,
};

const styles = StyleSheet.create({
  videoErrorImage: {
    height: Theme.px(80),
    width: Theme.px(122),
  },
  videoErrorText: {
    paddingLeft: Theme.px(15),
    width: Theme.px(275),
  },
  videoErrorWrapper: {
    alignItems: 'center',
    backgroundColor: Theme.errorBackgroundColor,
    flexDirection: 'row',
    height: '100%',
    justifyContent: 'center',
    width: '100%',
  },
  videoTileWrapper: {
    backgroundColor: Theme.transparent,
    borderRadius: Theme.tileBorderRadius,
    overflow:
      !isX1 && !isVizio && !(isTizen && getTizenVersion() <= 4) // Otherwise the video will not show properly on MWD
        ? 'hidden'
        : 'visible',
  },
  radialGradientBackgroundContainer: {
    height: '100%',
  },
});
